import React, { useEffect, useState } from "react";

/**
 * InstantPhotoReel
 * ----------------
 * Premium "instant camera" polaroid composition that sits BEHIND the hero logo.
 * - 4 polaroid cards are scattered around the logo in a balanced fan layout.
 * - A "highlight" cycles between them: a brief camera flash → that polaroid
 *   gets a subtle scale + glow + chrome border lift → its image gently swaps
 *   to the next photo from the queue.
 *
 * Replace PHOTOS entries with real WelkomBiz Photography assets when available.
 * Each entry: { src, category, date }.
 */
const PHOTOS = [
  { src: "https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80&auto=format&fit=crop", category: "Weddings", date: "06 · 2025" },
  { src: "https://images.unsplash.com/photo-1471295253337-3ceaaedca402?w=600&q=80&auto=format&fit=crop", category: "Sports", date: "03 · 2025" },
  { src: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=600&q=80&auto=format&fit=crop", category: "Corporate", date: "04 · 2025" },
  { src: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=600&q=80&auto=format&fit=crop", category: "Outdoor", date: "07 · 2025" },
  { src: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=600&q=80&auto=format&fit=crop", category: "Matric Farewell", date: "09 · 2025" },
  { src: "https://images.unsplash.com/photo-1529636798458-92182e662485?w=600&q=80&auto=format&fit=crop", category: "Engagements", date: "10 · 2025" },
  { src: "https://images.unsplash.com/photo-1519689680058-324335c77eba?w=600&q=80&auto=format&fit=crop", category: "Baby Showers", date: "08 · 2025" },
  { src: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&q=80&auto=format&fit=crop", category: "Promotional", date: "11 · 2025" },
];

// Fixed slot layout — positions in % of the reel container. Designed to
// flank the logo without ever covering it. Mobile-safe (slots collapse via CSS).
const SLOTS = [
  { id: 0, top: "6%",  left: "4%",   rotate: -8,  z: 2,  size: 1.0 },
  { id: 1, top: "10%", right: "4%",  rotate: 7,   z: 2,  size: 1.0 },
  { id: 2, bottom: "8%", left: "10%", rotate: -5,  z: 3,  size: 0.92 },
  { id: 3, bottom: "4%", right: "8%", rotate: 9,   z: 3,  size: 0.92 },
];

const CYCLE_MS = 3600;
const FLASH_MS = 220;

export const InstantPhotoReel = () => {
  // Each slot holds one image index from PHOTOS, evolving over time.
  const [slotImages, setSlotImages] = useState(() => SLOTS.map((_, i) => i % PHOTOS.length));
  const [active, setActive] = useState(0);
  const [flashing, setFlashing] = useState(false);
  const [nextImage, setNextImage] = useState(SLOTS.length); // next photo index to feed in

  useEffect(() => {
    let timer;
    let mounted = true;

    const tick = () => {
      if (!mounted) return;
      // 1. flash on
      setFlashing(true);
      // 2. after flash peak, swap image in the *next* slot then move highlight
      setTimeout(() => {
        if (!mounted) return;
        setActive((prevActive) => {
          const nextActive = (prevActive + 1) % SLOTS.length;
          setSlotImages((imgs) => {
            const copy = [...imgs];
            copy[nextActive] = nextImage % PHOTOS.length;
            return copy;
          });
          setNextImage((n) => n + 1);
          return nextActive;
        });
        setFlashing(false);
      }, FLASH_MS);

      timer = setTimeout(tick, CYCLE_MS);
    };

    timer = setTimeout(tick, 1400);
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [nextImage]);

  return (
    <div
      data-testid="instant-photo-reel"
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{ perspective: "1200px" }}
    >
      {/* Flash overlay */}
      <div
        className="absolute inset-0 transition-opacity ease-out"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.15) 30%, rgba(255,255,255,0) 60%)",
          opacity: flashing ? 1 : 0,
          transitionDuration: flashing ? "80ms" : "420ms",
          mixBlendMode: "screen",
        }}
      />

      {/* Polaroid slots */}
      {SLOTS.map((slot, i) => {
        const photo = PHOTOS[slotImages[i]];
        const isActive = i === active;
        return (
          <figure
            key={slot.id}
            data-testid={`polaroid-${i}`}
            className={`absolute polaroid ${isActive ? "polaroid-active" : ""}`}
            style={{
              top: slot.top,
              left: slot.left,
              right: slot.right,
              bottom: slot.bottom,
              zIndex: isActive ? 5 : slot.z,
              transform: `rotate(${slot.rotate}deg) scale(${slot.size * (isActive ? 1.06 : 1)})`,
            }}
          >
            <div className="polaroid-inner">
              <div className="polaroid-photo">
                <img
                  src={photo.src}
                  alt=""
                  loading="lazy"
                  draggable={false}
                  className="w-full h-full object-cover"
                />
                {/* developing veil that lifts on activation */}
                <span className={`polaroid-developer ${isActive ? "developed" : ""}`} />
              </div>
              <figcaption className="polaroid-caption">
                <span className="polaroid-cat">{photo.category}</span>
                <span className="polaroid-date">{photo.date}</span>
              </figcaption>
            </div>
          </figure>
        );
      })}
    </div>
  );
};

export default InstantPhotoReel;
